import React, { useState } from 'react';
import { Button, Form, notification, Pagination, Row, Space, Table, Tag, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import permissionService from '../../services/permissionManagementRequest';
import TableAction from '../common/TableAction';
import RenderModalForm from '../common/renderModalForm';

const ARRAY_COLOR = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime'];

const PermissionTable = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [createNew, setCreateNew] = useState(false);
    const [form] = Form.useForm();

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Query riêng có truyền page/limit
    const permissionQuery = useQuery({
        queryKey: ['list-permission-table', page, limit],
        queryFn: () => permissionService.getList({ page, limit }),
    });

    // Extract data + total từ response
    const rawData = permissionQuery?.data;
    const permissionData = Array.isArray(rawData) ? rawData : rawData?.data || rawData?.rows || [];
    const total = rawData?.total || rawData?.totalItems || permissionData.length || 0;

    const { mutate: submitForm, isPending: isLoading } = useMutation({
        mutationFn: createNew
            ? (data) => permissionService.create(data)
            : (data) => permissionService.update(data.id, data),
        onSuccess: () => {
            notification.success({ message: `${createNew ? 'Tạo mới' : 'Chỉnh sửa'} tính năng thành công` });
            permissionQuery.refetch();
            setIsModalOpen(false);
        },
        onError: (error) => {
            notification.error({ message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
        },
    });

    const initForm = {
        actions: [
            { check: true, name: 'Sửa' },
            { check: true, name: 'Xoá' },
            { check: false, name: 'Xem' },
            { check: false, name: 'Thêm' },
        ],
    };

    const fields = [
        { name: 'id', colClass: 'hidden' },
        { name: 'name', label: 'Tên tính năng', lg: 24, rules: [{ required: true }] },
        { name: 'key_menu', label: 'Key menu', lg: 24, rules: [{ required: true }] },
        { name: 'url', label: 'Url view', lg: 24 },
        { name: 'api', label: 'Api', lg: 24, rules: [{ required: true }] },
        {
            name: 'status', label: 'Trạng thái', lg: 24, type: 'select',
            rules: [{ required: true }],
            options: [
                { value: 1, label: 'Kích hoạt' },
                { value: 0, label: 'Chờ kích hoạt' },
            ],
            propsFormItem: { initialValue: 1 },
        },
        {
            type: 'html', lg: 24,
            content: <Typography.Text className="mt-9 text-lg">Danh sách action</Typography.Text>,
        },
        {
            name: 'actions', type: 'group', lg: 24,
            colClass: 'border border-gray-500 pt-2',
            titleButtonAdd: 'Thêm action',
            minItem: 1, maxItem: 6,
            filedsList: [
                { name: 'id', colClass: 'hidden', lg: 24 },
                { name: 'check', type: 'checkbox', lg: 1 },
                { name: 'name', placeholder: 'Tên action', lg: 6 },
                { name: 'api', placeholder: 'Nhập api', lg: 17 },
            ],
        },
    ];

    const handleSubmitForm = (data) => {
        const check = data.actions?.some((item) => (!item.api || !item.name) && item.check);
        if (check) {
            notification.warning({ message: 'Vui lòng nhập đủ thông tin cho action được chọn!' });
            return;
        }
        // Phải khớp ACTION_ID trong constants/seedPermissions.js, nếu không mỗi lần
        // lưu lại ở màn này action_id của action lạ sẽ bị ghi đè về 0.
        const NAME_TO_ID = { XEM: 1, THÊM: 2, SỬA: 3, XOÁ: 4, COPY: 5, 'NHẬP TÀI KHOẢN': 6 };
        data.actions = data.actions
            .filter((item) => item.check)
            .map((item) => ({
                name: item.name,
                api: item.api,
                action_id: NAME_TO_ID[item.name.toUpperCase()] || 0,
                id: item.id,
            }));
        submitForm(data);
    };

    const handlePaginationChange = (newPage, newLimit) => {
        setPage(newPage);
        setLimit(newLimit);
    };

    return (
        <>
            <RenderModalForm
                loadingSubmit={isLoading}
                modalTitle={createNew ? 'Tạo mới tính năng' : 'Chỉnh sửa tính năng'}
                isOpenModal={isModalOpen}
                handleCloseModal={() => setIsModalOpen(false)}
                modalWidth={800}
                onFinish={handleSubmitForm}
                fields={fields}
                form={form}
            />
            <Row justify="space-between" align="bottom" className="my-2 cursor-pointer">
                <Typography.Title level={5} className="mb-0">#PERMISSION</Typography.Title>
                <Button
                    className="bg-white border-violet-500 text-violet-500"
                    onClick={() => {
                        form.resetFields();
                        form.setFieldsValue(initForm);
                        setCreateNew(true);
                        setIsModalOpen(true);
                    }}
                >
                    Thêm mới tính năng
                </Button>
            </Row>

            <Table
                scroll={{ x: 1200 }}
                bordered
                className="min-w-full"
                loading={permissionQuery.isLoading || permissionQuery.isFetching}
                pagination={false}
                dataSource={permissionData.map((item, key) => ({ ...item, key }))}
                columns={[
                    {
                        title: 'STT', width: 60, align: 'center',
                        render: (_, __, index) => (page - 1) * limit + index + 1,
                    },
                    { title: 'Tên tính năng', dataIndex: 'name' },
                    { title: 'Key menu', dataIndex: 'key_menu' },
                    { title: 'Url view', dataIndex: 'url' },
                    { title: 'Api', dataIndex: 'api' },
                    {
                        title: 'Danh sách action', dataIndex: 'actions',
                        render: (value) => (
                            <Space direction="vertical" size={4} split={<hr />} className="w-full">
                                {(value || []).map((item, key) => (
                                    <div className="flex gap-2 items-center" key={key}>
                                        <Tag color={ARRAY_COLOR[key]}>{item.name}</Tag>
                                        <Typography.Text>{item.api}</Typography.Text>
                                    </div>
                                ))}
                            </Space>
                        ),
                    },
                    {
                        title: 'Trạng thái', dataIndex: 'status',
                        render: (value) => (
                            <Tag color={value ? 'cyan' : 'red'}>{value ? 'Kích hoạt' : 'Chờ kích hoạt'}</Tag>
                        ),
                    },
                    {
                        title: '',
                        render: (item) => (
                            <TableAction
                                record={item}
                                onEdit={(record) => {
                                    form.resetFields();
                                    form.setFieldsValue({
                                        ...record,
                                        actions: record.actions.map((i) => ({ ...i, check: true })),
                                    });
                                    setCreateNew(false);
                                    setIsModalOpen(true);
                                }}
                                onCopy={(record) => {
                                    form.resetFields();
                                    const { id, ...rest } = record;
                                    form.setFieldsValue({
                                        ...rest,
                                        actions: record.actions.map((i) => ({ ...i, check: true })),
                                    });
                                    setCreateNew(true);
                                    setIsModalOpen(true);
                                }}
                                onDelete={async (record) => {
                                    try {
                                        await permissionService.delete(record.id);
                                        permissionQuery.refetch();
                                    } catch (error) {
                                        console.error('Xóa tính năng thất bại:', error);
                                    }
                                }}
                            />
                        ),
                    },
                ]}
            />

            {/* Pagination */}
            {total > 0 && (
                <div className="py-3 flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                        Hiển thị <strong>{(page - 1) * limit + 1}</strong> - <strong>{Math.min(page * limit, total)}</strong> trên tổng <strong>{total}</strong>
                    </span>
                    <Pagination
                        current={page}
                        pageSize={limit}
                        total={total}
                        onChange={handlePaginationChange}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50']}
                        locale={{ items_per_page: '/ trang' }}
                    />
                </div>
            )}
        </>
    );
};

export default PermissionTable;
